import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle, ArrowLeft, Shield, User } from 'lucide-react';
import { createCheckoutSession } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Checkout = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // If auth is still loading, wait
    if (authLoading) return;
    
    // If user is not authenticated, redirect to sign-in
    // Store intent so they return here after sign-in
    if (!isAuthenticated) {
      localStorage.setItem('ohmnai_redirect_intent', '/checkout');
      navigate('/auth/email');
      return;
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleSubscribe = async () => {
    setIsProcessing(true);
    setError(null);
    
    try {
      const result = await createCheckoutSession();
      
      if (result.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = result.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'Failed to create checkout session');
      setIsProcessing(false);
    }
  };

  // Show loading state while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-mist via-mist-light to-fern flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-forest" />
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-mist via-mist-light to-fern">
      {/* Header */}
      <header className="border-b border-calm-border bg-fern-card/80 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <div 
            className="text-2xl font-bold text-calm-text cursor-pointer" 
            style={{fontFamily: 'DM Serif Display, serif'}}
            onClick={() => navigate('/')}
          >
            OHMNAI
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/circle')}
            className="text-calm-text-secondary hover:text-forest hover:bg-fern"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="bg-fern-card rounded-2xl p-10 border border-calm-border shadow-lg">
          <h1 className="text-3xl text-calm-text mb-6" style={{fontFamily: 'DM Serif Display, serif'}}>
            Join OHMNAI Circle
          </h1>

          {/* User info - calm confirmation they're recognized */}
          {user && (
            <div className="flex items-center gap-3 mb-6 p-4 bg-herbal-light/30 rounded-xl border border-herbal/30">
              <div className="w-10 h-10 bg-forest/10 rounded-full flex items-center justify-center">
                <User className="h-5 w-5 text-forest" />
              </div>
              <div>
                <p className="text-sm text-calm-text-secondary">Subscribing as</p>
                <p className="text-forest font-medium">{user.email}</p>
              </div>
            </div>
          )}

          {/* Subscription Details */}
          <div className="space-y-6 mb-8">
            <div className="flex justify-between items-center pb-6 border-b border-calm-border">
              <div>
                <h2 className="text-lg font-medium text-calm-text">Circle Membership</h2>
                <p className="text-calm-text-secondary">Monthly subscription</p>
              </div>
              <div className="text-2xl text-forest font-medium">$9/mo</div>
            </div>

            {/* What's Included */}
            <div>
              <h3 className="font-medium text-calm-text mb-3">What&apos;s included</h3>
              <ul className="space-y-2 text-forest">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-sage-icon" />
                  Long-term alignment memory
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-sage-icon" />
                  Pooled action suggestions
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-sage-icon" />
                  Quiet sponsorship opportunities
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-sage-icon" />
                  Platform influence and experiments
                </li>
              </ul>
            </div>

            {/* Terms */}
            <div className="bg-herbal-light/50 rounded-xl p-4 border border-herbal">
              <p className="text-sm text-forest leading-relaxed">
                Cancel anytime from your dashboard. No questions asked. No penalties. Your free account remains fully functional.
              </p>
            </div>

            {/* Error message */}
            {error && (
              <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="space-y-4">
            <Button
              onClick={handleSubscribe}
              disabled={isProcessing}
              className="w-full bg-forest hover:bg-forest-dark text-white py-6 text-base rounded-xl transition-colors"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Connecting to Stripe...
                </>
              ) : (
                'Subscribe for $9/month'
              )}
            </Button>
            <Button
              onClick={() => navigate('/circle')}
              variant="outline"
              disabled={isProcessing}
              className="w-full border-calm-border text-forest hover:bg-fern py-6 text-base rounded-xl transition-colors"
            >
              Back to Circle
            </Button>
          </div>

          <div className="flex items-center justify-center gap-2 mt-6 text-calm-text-secondary">
            <Shield className="h-4 w-4" />
            <p className="text-xs">
              Payments processed securely by Stripe
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
